import React, { useCallback } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { useModalMinimize } from 'contexts/ModalMinimizeContext';
import CreditEligiblePill from 'components/common/CreditEligiblePill';
import type { Corporate } from 'api/types';

// ============================================================================
// TYPE DEFINITIONS
// ============================================================================

export interface CorporateViewModalProps {
  isOpen: boolean;
  corporate: Corporate | null;
  onClose: () => void;
  onEdit?: (corporate: Corporate) => void;
  // Minimize-to-bar support
  tabId?: string;
  moduleName?: string;
  onRestoreView?: (corporateId: string) => void;
}

// ============================================================================
// READ-ONLY FIELD
// ============================================================================

const ReadOnlyField: React.FC<{ label: string; value?: string | number | boolean | null; span?: boolean }> = ({ label, value, span }) => (
  <div style={span ? { gridColumn: '1 / -1' } : undefined}>
    <label className="form-label">{label}</label>
    <div style={{ padding: '0.5rem', background: 'var(--bg-secondary)', borderRadius: '0.375rem', border: '1px solid var(--form-border-color)' }}>
      {value != null && value !== '' ? (typeof value === 'boolean' ? (value ? 'Yes' : 'No') : String(value)) : '-'}
    </div>
  </div>
);

// ============================================================================
// CONSTANTS
// ============================================================================

const DISCOUNT_TYPE_LABELS: Record<string, string> = {
  Percentage: 'Percentage',
  'Flat Amount': 'Flat Amount',
};

// ============================================================================
// COMPONENT
// ============================================================================

const CorporateViewModal: React.FC<CorporateViewModalProps> = ({
  isOpen,
  corporate,
  onClose,
  onEdit,
  tabId,
  moduleName,
  onRestoreView,
}) => {
  const { minimize } = useModalMinimize();

  const handleMinimize = useCallback(() => {
    if (!tabId || !moduleName || !corporate) return;
    minimize({
      id: `${tabId}-view-modal-${corporate.id}`,
      title: `View - ${corporate.corporateCode}`,
      moduleName,
      tabId,
      onRestore: () => onRestoreView?.(corporate.id),
    });
    onClose();
  }, [tabId, moduleName, corporate, minimize, onClose, onRestoreView]);

  if (!isOpen || !corporate) return null;

  const formatCurrency = (val?: number) => val != null ? val.toFixed(2) : '-';
  const formatDate = (date?: string) => {
    if (!date) return '-';
    try { return new Date(date).toLocaleDateString(); } catch { return '-'; }
  };

  return (
    <>
      <div className="modal-backdrop" />
      <div className="modal-container modal-lg" style={{ minHeight: 'auto' }}>
        <div className="modal-header">
          <h2 className="modal-title">Corporate Details</h2>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
            {tabId && (
              <button className="modal-header-btn" onClick={handleMinimize} title="Minimize to bar">
                <IconSystem.chevronDown size={16} />
              </button>
            )}
            <button className="modal-close" onClick={onClose}>
              <IconSystem.x size={20} />
            </button>
          </div>
        </div>

        <div className="modal-content" style={{ maxHeight: '70vh', overflowY: 'auto' }}>
          {/* --- Basic Information --- */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '1.5rem' }}>
            <ReadOnlyField label="Corporate Code" value={corporate.corporateCode} />
            <ReadOnlyField label="Company Name" value={corporate.companyName} />
            <ReadOnlyField label="Industry" value={corporate.industry} />
          </div>

          {/* --- Status --- */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem', marginTop: '1rem' }}>
            <div>
              <label className="form-label">Status</label>
              <div>
                <span className={`badge badge-${corporate.isActive ? 'active' : 'inactive'}`}>
                  {corporate.isActive ? 'Active' : 'Inactive'}
                </span>
              </div>
            </div>
          </div>

          {/* --- Contact Information --- */}
          <div style={{ marginTop: '1.25rem', borderTop: '1px solid var(--border-color)', paddingTop: '1rem' }}>
            <h3 style={{ fontSize: '0.9rem', fontWeight: 600, color: 'var(--text-primary)', marginBottom: '0.75rem' }}>Contact Information</h3>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '1.5rem' }}>
              <ReadOnlyField label="Contact Person" value={corporate.contactPerson} />
              <ReadOnlyField label="Designation" value={corporate.designation} />
              <ReadOnlyField label="Phone" value={corporate.phone} />
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem', marginTop: '1rem' }}>
              <ReadOnlyField label="Email" value={corporate.email} />
              <ReadOnlyField label="Website" value={corporate.website} />
            </div>
          </div>

          {/* --- Billing Address --- */}
          <div style={{ marginTop: '1.25rem', borderTop: '1px solid var(--border-color)', paddingTop: '1rem' }}>
            <h3 style={{ fontSize: '0.9rem', fontWeight: 600, color: 'var(--text-primary)', marginBottom: '0.75rem' }}>Billing</h3>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '1.5rem' }}>
              <ReadOnlyField label="Billing Address" value={corporate.billingAddress} />
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem', marginTop: '1rem' }}>
              <ReadOnlyField label="GST Number" value={corporate.gstNumber} />
              <ReadOnlyField label="PAN Number" value={corporate.panNumber} />
            </div>
          </div>

          {/* --- Credit --- */}
          <div style={{ marginTop: '1.25rem', borderTop: '1px solid var(--border-color)', paddingTop: '1rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.75rem' }}>
              <h3 style={{ fontSize: '0.9rem', fontWeight: 600, color: 'var(--text-primary)', margin: 0 }}>Credit</h3>
              <CreditEligiblePill eligible={corporate.isCreditEligible} />
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem' }}>
              <ReadOnlyField label="Credit Days" value={corporate.creditDays} />
              <ReadOnlyField label="Credit Limit" value={formatCurrency(corporate.creditLimit)} />
            </div>
          </div>

          {/* --- Agreement & Discount --- */}
          <div style={{ marginTop: '1.25rem', borderTop: '1px solid var(--border-color)', paddingTop: '1rem' }}>
            <h3 style={{ fontSize: '0.9rem', fontWeight: 600, color: 'var(--text-primary)', marginBottom: '0.75rem' }}>Agreement & Discount</h3>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem' }}>
              <ReadOnlyField label="Agreement Start" value={formatDate(corporate.agreementStart)} />
              <ReadOnlyField label="Agreement End" value={formatDate(corporate.agreementEnd)} />
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '1.5rem', marginTop: '1rem' }}>
              <ReadOnlyField label="Discount Type" value={corporate.discountType ? (DISCOUNT_TYPE_LABELS[corporate.discountType] || corporate.discountType) : undefined} />
              <ReadOnlyField label="Discount Value" value={formatCurrency(corporate.discountValue)} />
              <ReadOnlyField label="Include Taxes" value={corporate.includeTaxes} />
            </div>
          </div>
        </div>

        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>
            Close
          </button>
          {onEdit && (
            <button className="btn btn-primary" onClick={() => {
              onClose();
              onEdit(corporate);
            }}>
              <IconSystem.edit size={16} />
              Edit
            </button>
          )}
        </div>
      </div>
    </>
  );
};

export default CorporateViewModal;
